import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';

export interface FilterState {
  category: string;
  categories: string[];
  types: string[];
  priceFilter: string[];
}

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  items: any[];
  config: any;
  translations: Record<string, Record<string, string>>;
  categoryColors: Map<string, string>;
}

const PRICE_LEVELS = ['€', '€€', '€€€', '€€€€'];

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  items,
  config,
  translations,
  categoryColors,
}) => {
  const { i18n } = useTranslation();

  const categories = useMemo(() => {
    const unique = Array.from(new Set(items.map((i) => i.category))).filter(Boolean);
    return unique.sort((a, b) => {
      const at = translations[a]?.[i18n.language] || translations[a]?.fr || a;
      const bt = translations[b]?.[i18n.language] || translations[b]?.fr || b;
      return at.localeCompare(bt);
    });
  }, [items, translations, i18n.language]);

  const activeCategories = filters.categories.length > 0 ? filters.categories : (filters.category ? [filters.category] : []);

  const types = useMemo(() => {
    if (activeCategories.length === 0) return [];
    const unique = Array.from(
      new Set(items.filter((i) => activeCategories.includes(i.category)).map((i) => i.type))
    ).filter(Boolean);
    return unique.sort((a, b) => {
      const at = translations[a]?.[i18n.language] || translations[a]?.fr || a;
      const bt = translations[b]?.[i18n.language] || translations[b]?.fr || b;
      return at.localeCompare(bt);
    });
  }, [items, activeCategories, translations, i18n.language]);

  const availablePriceLevels = useMemo(() => {
    const found = new Set(items.map((i: any) => i.price?.trim()).filter(Boolean));
    const levels = PRICE_LEVELS.filter((p) => found.has(p));
    return levels.length > 1 ? levels : [];
  }, [items]);

  const handleCategoryChange = (category: string) => {
    if (!category) {
      onFiltersChange({ ...filters, category: '', categories: [], types: [] });
      return;
    }
    const current = activeCategories;
    const isAlready = current.includes(category);
    const next = isAlready ? current.filter(c => c !== category) : [...current, category];
    onFiltersChange({ ...filters, category: next.length === 1 ? next[0] : '', categories: next, types: [] });
  };

  const handleTypeToggle = (type: string) => {
    const next = filters.types.includes(type)
      ? filters.types.filter((t) => t !== type)
      : [...filters.types, type];
    onFiltersChange({ ...filters, types: next });
  };

  const handleClearTypes = () => {
    onFiltersChange({ ...filters, types: [] });
  };

  const handlePriceToggle = (level: string) => {
    const next = filters.priceFilter.includes(level)
      ? filters.priceFilter.filter((p) => p !== level)
      : [...filters.priceFilter, level];
    onFiltersChange({ ...filters, priceFilter: next });
  };

  const handleReset = () => {
    onFiltersChange({ category: '', categories: [], types: [], priceFilter: [] });
  };

  const activeCount =
    activeCategories.length +
    filters.types.length +
    filters.priceFilter.length;

  const chipBase = 'px-2.5 py-1 text-xs font-medium transition-all border rounded';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-[200]"
            onClick={onClose}
          />

          <motion.div
            key="drawer"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed left-0 right-0 z-[400] bg-white rounded-t-xl shadow-2xl"
            style={{ bottom: '40px', maxHeight: 'calc(80vh - 40px)' }}
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  {getTranslatedText(translations, 'filter_drawer_title', i18n.language) || 'Filtres'}
                </h2>
                {activeCount > 0 && (
                  <span
                    className="text-xs font-bold px-1.5 py-0.5 rounded text-white"
                    style={{ backgroundColor: config['major-color'] }}
                  >
                    {activeCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={handleReset}
                    className="text-xs font-medium"
                    style={{ color: config['major-color'] }}
                  >
                    {getTranslatedText(translations, 'filter_reset', i18n.language) || 'Réinitialiser'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            <div
              className="px-4 pb-6 overflow-y-auto"
              style={{ maxHeight: 'calc(80vh - 100px)' }}
            >
              <div className="w-8 h-0.5 bg-gray-200 rounded-full mx-auto mt-3 mb-4" />

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                    {getTranslatedText(translations, 'filter_category_label', i18n.language) || 'Catégorie'}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleCategoryChange('')}
                      className={chipBase}
                      style={
                        activeCategories.length === 0
                          ? { backgroundColor: config['major-color'], color: '#fff', borderColor: config['major-color'] }
                          : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                      }
                    >
                      {getTranslatedText(translations, 'filter_all_categories', i18n.language) || 'Toutes'}
                    </button>
                    {categories.map((cat) => {
                      const isActive = activeCategories.includes(cat);
                      const color = categoryColors.get(cat) || config['medium-color'];
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => handleCategoryChange(cat)}
                          className={chipBase}
                          style={
                            isActive
                              ? { backgroundColor: color, color: '#fff', borderColor: color }
                              : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                          }
                        >
                          {getTranslatedCategory(translations, cat, i18n.language)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {activeCategories.length > 0 && types.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        {getTranslatedText(translations, 'filter_type_label', i18n.language) || 'Type'}
                      </label>
                      {filters.types.length > 0 && (
                        <button
                          type="button"
                          onClick={handleClearTypes}
                          className="text-[10px] font-medium"
                          style={{ color: config['major-color'] }}
                        >
                          {getTranslatedText(translations, 'filter_reset', i18n.language) || 'Réinitialiser'}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {types.map((t) => {
                        const isActive = filters.types.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => handleTypeToggle(t)}
                            className={chipBase}
                            style={
                              isActive
                                ? { backgroundColor: config['medium-color'], color: '#fff', borderColor: config['medium-color'] }
                                : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                            }
                          >
                            {getTranslatedCategory(translations, t, i18n.language)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {availablePriceLevels.length > 0 && (
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                      {getTranslatedText(translations, 'filter_price_label', i18n.language) || 'Prix'}
                    </label>
                    <div className="flex gap-1.5">
                      {availablePriceLevels.map((level) => {
                        const isActive = filters.priceFilter.includes(level);
                        return (
                          <button
                            key={level}
                            type="button"
                            onClick={() => handlePriceToggle(level)}
                            className={`${chipBase} min-w-[42px] text-center`}
                            style={
                              isActive
                                ? { backgroundColor: config['major-color'], color: '#fff', borderColor: config['major-color'] }
                                : { backgroundColor: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                            }
                          >
                            {level}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-5">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: config['major-color'] }}
                >
                  {getTranslatedText(translations, 'filter_apply', i18n.language) || 'Appliquer'}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
